import { LABELS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Add, Delete, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { ISingleProduct } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ProductApi } from 'src/api/product.api'
import { ProductFormModal } from 'src/partials/modals/products/productForm.modal'

export const ProductSettingsForm = observer(() => {
  const { company } = useStore()
  const isMobile = useIsMobile()
  const productApi = new ProductApi()
  const [products, setProducts] = useState<ISingleProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ISingleProduct | null>(null)

  useEffect(() => {
    loadProducts()
  }, [])

  const loadProducts = async () => {
    try {
      setLoading(true)
      const data = await productApi.getAll()
      setProducts(data)
    } catch (error) {
      console.error('Failed to load products:', error)
      toast.error('Fout bij het laden van producten')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Weet je zeker dat je dit product wilt verwijderen?')) {
      try {
        await productApi.delete(id)
        toast.success('Product succesvol verwijderd')
        loadProducts()
      } catch (error: any) {
        console.error('Failed to delete product:', error)
        toast.error(error?.response?.data?.message || 'Fout bij het verwijderen van product')
      }
    }
  }

  const handleCreate = () => {
    setEditingProduct(null)
    setModalOpen(true)
  }

  const handleEdit = (product: ISingleProduct) => {
    setEditingProduct(product)
    setModalOpen(true)
  }

  const handleSave = async (productData: Partial<ISingleProduct>) => {
    try {
      if (editingProduct) {
        await productApi.update(editingProduct.id, productData)
        toast.success('Product succesvol bijgewerkt')
      } else {
        // Validate that we have a company_id
        if (!company.current_company?.id) {
          toast.error('Bedrijfsinformatie niet beschikbaar. Probeer de pagina te vernieuwen.')
          throw new Error('Company ID is not available')
        }

        const createData: Partial<ISingleProduct> = {
          ...productData,
          company_id: company.current_company.id,
        }
        await productApi.create(createData)
        toast.success('Product succesvol aangemaakt')
      }
      loadProducts()
    } catch (error: any) {
      console.error('Failed to save product:', error)
      toast.error(error?.response?.data?.message || 'Fout bij het opslaan van product')
      throw error
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingProduct(null)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
        }}>
        <Typography variant="body2">
          Beheer je productcatalogus. Deze producten kun je snel toevoegen aan facturen en offertes.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="small"
          onClick={handleCreate}
          sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {LABELS.PRODUCT_CREATE}
        </Button>
      </Stack>

      {products.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="body2"
            color="text.secondary">
            Nog geen producten toegevoegd. Klik op &quot;Nieuw product&quot; om te beginnen.
          </Typography>
        </Paper>
      ) : isMobile ? (
        /* Mobile: Card layout */
        <Stack spacing={2}>
          {products.map(product => (
            <Card key={product.id}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{ mt: 0.5 }}>
                        {product.formatted_price || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Bewerken">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(product)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Verwijderen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(product.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {product.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary">
                      {product.description}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        /* Desktop: Table layout */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{LABELS.TITLE}</TableCell>
                <TableCell>{LABELS.EXTRA_DESCRIPTION}</TableCell>
                <TableCell align="right">{LABELS.UNIT_PRICE}</TableCell>
                <TableCell align="right">Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map(product => (
                <TableRow
                  key={product.id}
                  hover>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>
                    {product.description ? (
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {product.description}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">{product.formatted_price || '-'}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Bewerken">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(product)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Verwijderen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(product.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ProductFormModal
        open={modalOpen}
        product={editingProduct}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </Stack>
  )
})
